import { OrderModel } from 'lib/model/objects/orderModel';
import { isAnonymousOrder } from 'lib/types/order';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { ColumnService } from 'lib/services/directory';
import { logAndCaptureException } from 'utils';
import { BillingTermType } from 'lib/enums';
import { ConfigurationError } from 'lib/errors/ColumnErrors';
import { getNewspaperErrorInfoForNewspaperIdWithDefaults } from 'lib/errors/templateErrors';
import { mapAsync } from 'lib/helpers/array/async';
import { getFirebaseContext } from 'utils/firebase';
import { NewspaperOrderService } from 'lib/services/newspaperOrderService';
import { NewspaperOrdersFormData } from '../PlacementFlowStepSelector';

/**
 * Determines whether an order should be invoiced outside Column (IOC) and detects conflicting terms.
 * Rules:
 *  - If at least one customer/customerOrganization billing term is outside_column and no others are upfront_payment -> IOC true.
 *  - If mix of outside_column and upfront_payment -> conflict (log for CSMS) and IOC false.
 *  - If none outside_column -> IOC false.
 */
export const computeOrderIOC = async (
  orderModel: OrderModel,
  newspaperOrdersFormData: NewspaperOrdersFormData
): Promise<ResponseOrError<boolean>> => {
  const ctx = getFirebaseContext();
  const orderData = orderModel.modelData;
  if (isAnonymousOrder(orderData)) {
    return wrapSuccess(false);
  }
  type TermSource = {
    newspaperId: string;
    billingTerm: number;
  };
  const service = new NewspaperOrderService(ctx);
  const [termSourceError, termSources] = await mapAsync(
    newspaperOrdersFormData,
    async no => {
      if (!no.newspaper) {
        return wrapSuccess(null);
      }
      const [termErr, term] = await service.getOperativeBillingTerm(
        orderModel.modelData,
        no
      );
      if (termErr) {
        logAndCaptureException(
          ColumnService.ORDER_PLACEMENT,
          termErr,
          'Error computing operative billing term for IOC',
          { orderId: orderModel.id, newspaperId: no.newspaper.id }
        );
        return wrapError(termErr);
      }
      return wrapSuccess({
        newspaperId: no.newspaper.id,
        billingTerm: term
      });
    }
  );
  if (termSourceError) {
    return wrapError(termSourceError);
  }
  const nonNullSources = termSources.filter((t): t is TermSource => t !== null);
  const iocTermSources = nonNullSources.filter(
    t => t.billingTerm === BillingTermType.outside_column.value
  );
  const rupTermSources = nonNullSources.filter(
    t => t.billingTerm === BillingTermType.upfront_payment.value
  );

  if (iocTermSources.length > 0 && rupTermSources.length > 0) {
    // Conflict: fetch newspaper infos for all involved newspapers
    const newspaperIds = [
      ...iocTermSources.map(t => t.newspaperId),
      ...rupTermSources.map(t => t.newspaperId)
    ];
    const uniqueIds = Array.from(new Set(newspaperIds));
    const [infoError, newspaperInfos] = await mapAsync(uniqueIds, async id =>
      getNewspaperErrorInfoForNewspaperIdWithDefaults(ctx, id)
    );
    if (infoError) {
      logAndCaptureException(
        ColumnService.ORDER_MANAGEMENT,
        infoError,
        'Error while aggregating newspaper infos for IOC conflict logging',
        { orderId: orderModel.id }
      );
    } else {
      const error = new ConfigurationError({
        message: 'Conflicting billing terms (IOC & upfront_payment) on order',
        toFix:
          'Align billing terms across customer/customerOrganization records (remove mix of IOC and upfront)',
        newspaperInfo: newspaperInfos
      });
      logAndCaptureException(
        ColumnService.CSMS,
        error,
        'IOC / Upfront billing term conflict detected',
        {
          orderId: orderModel.id,
          iocNewspaperIds: iocTermSources.map(t => t.newspaperId).join(','),
          rupNewspaperIds: rupTermSources.map(t => t.newspaperId).join(',')
        }
      );
    }
  }

  const isIOC = iocTermSources.length > 0 && rupTermSources.length === 0;
  return wrapSuccess(isIOC);
};
